// Admin moderation queue for user-generated property reviews
// (epic PROP-72 / story PROP-77).
//
// Three tabs (Reported / Hidden / All) over the same paginated list
// endpoint. Each row exposes Hide / Restore / Delete actions that pop
// a dialog forcing a free-text reason — the reason rides through to
// the admin_audit_log row written server-side so a future moderator
// can reconstruct who acted on what and why.
//
// Aligned with ReviewQueue.tsx for chrome (PageHeader, table styles,
// toast stack); no parallel design system.

import { useEffect, useState } from "react";
import { PageHeader, primaryBtn, ghostBtn } from "@/components/admin-ui";
import { Modal, useToast } from "@/components/ui-prims";
import {
  deleteReviewAsAdmin,
  hideReview,
  listModerationReviews,
  restoreReview,
  type ModerationFilter,
  type ModerationReview,
} from "@/lib/property-reviews-api";

type Verb = "hide" | "restore" | "delete";

const TABS: { key: ModerationFilter; label: string }[] = [
  { key: "reported", label: "Reported" },
  { key: "hidden", label: "Hidden" },
  { key: "all", label: "All" },
];

const PAGE_LIMIT = 30;

export function PropertyReviewsModerationPage() {
  const toast = useToast();

  const [filter, setFilter] = useState<ModerationFilter>("reported");
  const [rows, setRows] = useState<ModerationReview[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [loadingMore, setLoadingMore] = useState(false);

  // The action dialog is single-instance — only one row at a time can
  // be acted on. State carries the row + chosen verb + reason draft.
  const [pending, setPending] = useState<{ row: ModerationReview; verb: Verb } | null>(null);
  const [reason, setReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Refresh whenever the active tab changes. The cleanup token guards
  // against a stale resolution: if the user switched tabs (or unmounted
  // the page) before the fetch returned, `alive` is false and we
  // commit nothing. Doing the guard via a flag instead of a setFilter
  // updater keeps React's setState updater contract pure — anti-pattern
  // side effects inside an updater would fire twice in StrictMode.
  useEffect(() => {
    let alive = true;
    setStatus("loading");
    setRows([]);
    setNextCursor(undefined);
    setExpanded(new Set());
    listModerationReviews({ filter, limit: PAGE_LIMIT })
      .then((resp) => {
        if (!alive) return;
        setRows(resp.reviews ?? []);
        setNextCursor(resp.next_cursor);
        setStatus("ready");
      })
      .catch((err) => {
        if (!alive) return;
        console.error("listModerationReviews", err);
        setStatus("error");
      });
    return () => {
      alive = false;
    };
  }, [filter]);

  const onLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const resp = await listModerationReviews({
        filter,
        limit: PAGE_LIMIT,
        cursor: nextCursor,
      });
      setRows((prev) => [...prev, ...(resp.reviews ?? [])]);
      setNextCursor(resp.next_cursor);
    } catch (err) {
      console.error("listModerationReviews more", err);
      toast("Couldn't load more reviews", { tone: "error" });
    } finally {
      setLoadingMore(false);
    }
  };

  const askAction = (row: ModerationReview, verb: Verb) => {
    setPending({ row, verb });
    setReason("");
    setActionErr(null);
  };

  const confirmAction = async () => {
    if (!pending) return;
    const trimmed = reason.trim();
    if (trimmed === "") {
      setActionErr("Reason required — explain why this action is being taken so the audit row reads cleanly.");
      return;
    }
    setActionBusy(true);
    setActionErr(null);
    const { row, verb } = pending;
    try {
      const resp =
        verb === "hide"
          ? await hideReview(row.id, trimmed)
          : verb === "restore"
          ? await restoreReview(row.id, trimmed)
          : await deleteReviewAsAdmin(row.id, trimmed);
      // Patch the row from the server response — never from local
      // state. Then prune rows that no longer belong on the current
      // tab so the list reflects the moderator's action immediately
      // (a "restored" row drops out of the Reported tab; a "deleted"
      // row drops out of every tab since deleted is excluded).
      setRows((prev) => {
        const patched = prev.map((r) => (r.id === row.id ? resp.review : r));
        return filterRowsForTab(patched, filter);
      });
      setPending(null);
      setReason("");
      toast(`Review ${verbPastTense(verb)} · audit logged`, { tone: "success" });
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e?.status === 404) {
        setActionErr("This review has already been removed by another moderator.");
      } else {
        setActionErr(e?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setActionBusy(false);
    }
  };

  const cancelAction = () => {
    if (actionBusy) return;
    setPending(null);
    setReason("");
    setActionErr(null);
  };

  return (
    <>
      <PageHeader
        kicker="Moderation"
        title="Property reviews"
        sub="Reported / hidden user-generated reviews on property detail modals. Hide takes a row out of the public feed; Restore puts it back; Delete is a soft-delete that surfaces only in the audit trail. Every action requires a reason that lands in admin_audit_log."
      />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, padding: "0 24px", borderBottom: "1px solid var(--rule)" }}>
        {TABS.map((tab) => {
          const active = filter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 14px",
                cursor: "pointer",
                fontFamily: "var(--font-ui)",
                fontSize: 12.5,
                fontWeight: active ? 500 : 400,
                color: active ? "var(--accent)" : "var(--ink-3)",
                borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div style={{ padding: "16px 24px 32px" }}>
        {status === "loading" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>Loading reviews…</p>
        ) : status === "error" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)" }}>
            Couldn't load reviews. Refresh the page to try again.
          </p>
        ) : rows.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>
            Nothing in this queue right now.
          </p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-ui)" }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: 10.5, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                <th style={th()}>Project</th>
                <th style={th()}>Author</th>
                <th style={th()}>★</th>
                <th style={{ ...th(), width: "40%" }}>Body</th>
                <th style={th()}>Status</th>
                <th style={th()}>Reports</th>
                <th style={th()}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ReviewRow
                  key={r.id}
                  row={r}
                  expanded={expanded.has(r.id)}
                  onToggleExpanded={() => toggleExpanded(r.id)}
                  onAction={(verb) => askAction(r, verb)}
                />
              ))}
            </tbody>
          </table>
        )}

        {nextCursor && status === "ready" && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <button type="button" style={ghostBtn} onClick={() => void onLoadMore()} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load older reviews"}
            </button>
          </div>
        )}
      </div>

      {/* Action dialog */}
      <Modal
        open={!!pending}
        onClose={cancelAction}
        title={pending ? dialogTitle(pending.verb) : ""}
        width={520}
        footer={
          <>
            <button type="button" style={ghostBtn} onClick={cancelAction} disabled={actionBusy}>
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...primaryBtn,
                background: pending?.verb === "delete" ? "#7a2d1f" : primaryBtn.background,
              }}
              onClick={() => void confirmAction()}
              disabled={actionBusy}
            >
              {actionBusy ? "Working…" : pending ? dialogConfirm(pending.verb) : ""}
            </button>
          </>
        }
      >
        {pending && (
          <>
            <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55, margin: "0 0 12px" }}>
              {dialogBody(pending.verb)}
            </p>
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 6, background: "var(--surface-2)", fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 }}>
              <strong>{pending.row.project_name}</strong> · {pending.row.author_name || "Anonymous"} · ★ {pending.row.rating}
              <br />
              <span style={{ color: "var(--ink-3)" }}>{truncate(pending.row.body, 200)}</span>
            </div>
            <label style={{ display: "block", marginBottom: 8 }}>
              <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Reason <span style={{ color: "#7a2d1f" }}>*</span>
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Why this action? — auto-recorded in admin_audit_log so the next moderator can trace it."
                disabled={actionBusy}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--rule)",
                  fontSize: 13,
                  fontFamily: "var(--font-ui)",
                  background: "var(--surface)",
                  color: "var(--ink)",
                  resize: "vertical",
                  minHeight: 70,
                }}
              />
            </label>
            {actionErr && (
              <p style={{ fontSize: 12, color: "#7a2d1f", margin: "4px 0 0" }}>{actionErr}</p>
            )}
          </>
        )}
      </Modal>
    </>
  );
}

// ── sub-components / helpers ──────────────────────────────────────────

function ReviewRow({
  row,
  expanded,
  onToggleExpanded,
  onAction,
}: {
  row: ModerationReview;
  expanded: boolean;
  onToggleExpanded: () => void;
  onAction: (verb: Verb) => void;
}) {
  const stars = "★★★★★".slice(0, row.rating).padEnd(5, "☆");
  return (
    <>
      <tr style={{ borderTop: "1px solid var(--rule)" }}>
        <td style={td()}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink)", overflowWrap: "anywhere" }}>{row.project_name}</div>
          <div style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 2 }}>{relativeTime(row.created_at)}</div>
        </td>
        <td style={td()}>
          <div style={{ fontSize: 12, color: "var(--ink-2)", overflowWrap: "anywhere" }}>{row.author_name || "Anonymous"}</div>
          <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>{row.author_user_id.slice(0, 8)}</div>
        </td>
        <td style={td()}>
          <span style={{ color: "var(--accent)", letterSpacing: 1 }}>{stars}</span>
        </td>
        <td style={{ ...td(), fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>
          <span style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" } as React.CSSProperties}>
            {row.body}
          </span>
        </td>
        <td style={td()}>
          <StatusPill status={row.status} />
        </td>
        <td style={td()}>
          {row.reports.length > 0 ? (
            <button
              type="button"
              onClick={onToggleExpanded}
              style={{ background: "transparent", border: "1px solid var(--rule)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "var(--ink-2)", cursor: "pointer", fontFamily: "var(--font-ui)" }}
            >
              {row.reports.length} {expanded ? "▴" : "▾"}
            </button>
          ) : (
            <span style={{ fontSize: 11, color: "var(--ink-4)" }}>0</span>
          )}
        </td>
        <td style={td()}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {row.status === "visible" && (
              <ActionBtn onClick={() => onAction("hide")}>Hide</ActionBtn>
            )}
            {row.status === "hidden" && (
              <ActionBtn onClick={() => onAction("restore")}>Restore</ActionBtn>
            )}
            <ActionBtn danger onClick={() => onAction("delete")}>Delete</ActionBtn>
          </div>
        </td>
      </tr>
      {expanded && row.reports.length > 0 && (
        <tr>
          <td colSpan={7} style={{ padding: "0 12px 12px", background: "var(--surface-2)" }}>
            <div style={{ padding: 12, fontSize: 12, color: "var(--ink-2)" }}>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                Reporters ({row.reports.length})
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {row.reports.map((rp, i) => (
                  <li key={`${rp.reporter_user_id}-${i}`} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ fontWeight: 500, color: "var(--ink-2)", flexShrink: 0 }}>{rp.reporter_name || "Anonymous"}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--accent)", flexShrink: 0 }}>{rp.reason}</span>
                    {rp.note && <span style={{ color: "var(--ink-3)", overflowWrap: "anywhere" }}>{rp.note}</span>}
                    <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--ink-4)", flexShrink: 0 }}>{relativeTime(rp.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ActionBtn({ children, danger, onClick }: { children: React.ReactNode; danger?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: danger ? "transparent" : "var(--accent-soft)",
        border: `1px solid ${danger ? "#7a2d1f" : "var(--accent)"}`,
        color: danger ? "#7a2d1f" : "var(--accent)",
        borderRadius: 6,
        padding: "4px 10px",
        fontSize: 11,
        fontWeight: 500,
        fontFamily: "var(--font-ui)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function StatusPill({ status }: { status: ModerationReview["status"] }) {
  const palette =
    status === "visible"
      ? { bg: "var(--accent-soft)", fg: "var(--accent)" }
      : status === "hidden"
      ? { bg: "#f0e7d0", fg: "#6b5320" }
      : { bg: "#ecd9d2", fg: "#7a2d1f" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 12,
        background: palette.bg,
        color: palette.fg,
        fontSize: 10.5,
        fontWeight: 500,
        letterSpacing: "0.02em",
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );
}

function th(): React.CSSProperties {
  return { padding: "10px 8px", borderBottom: "1px solid var(--rule)", fontWeight: 500 };
}

function td(): React.CSSProperties {
  return { padding: "12px 8px", verticalAlign: "top" };
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

function verbPastTense(v: Verb): string {
  return v === "hide" ? "hidden" : v === "restore" ? "restored" : "deleted";
}

function dialogTitle(v: Verb): string {
  return v === "hide" ? "Hide this review?" : v === "restore" ? "Restore this review?" : "Delete this review?";
}

function dialogConfirm(v: Verb): string {
  return v === "hide" ? "Hide review" : v === "restore" ? "Restore review" : "Delete review";
}

function dialogBody(v: Verb): string {
  switch (v) {
    case "hide":
      return "This will pull the review out of the public feed immediately. Reporters will not be notified. Hidden rows stay in the queue under the Hidden tab.";
    case "restore":
      return "This will put the review back in the public feed. The reports against it will remain on the row as moderation history.";
    case "delete":
      return "Soft-delete — the body is kept for the audit trail but the row no longer surfaces in any tab. Reserve for the worst cases; Hide is the normal moderation action.";
  }
}

// filterRowsForTab drops rows that no longer belong on the current tab
// after a moderator's action — a "restore"d row leaves Hidden; a
// "delete"d row leaves every tab; a row whose report_count would no
// longer match Reported (set on the server) is left as-is for now,
// since report_count doesn't change in this mutation flow.
function filterRowsForTab(rows: ModerationReview[], filter: ModerationFilter): ModerationReview[] {
  switch (filter) {
    case "reported":
      return rows.filter((r) => r.status === "visible" && r.report_count > 0);
    case "hidden":
      return rows.filter((r) => r.status === "hidden");
    case "all":
      return rows.filter((r) => r.status === "visible" || r.status === "hidden");
  }
}

// relativeTime: small inline helper — the admin shell doesn't import
// the consumer's relativeTime, so we inline a copy to keep this page
// self-contained. Boundaries match the consumer's exact thresholds
// (min / hrs / days / locale date) so the FE reads the same way
// regardless of which surface a moderator is looking at.
function relativeTime(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diffMin = Math.max(0, (Date.now() - t) / 60000);
  if (diffMin < 60) return `${Math.round(diffMin)} min ago`;
  const diffHr = diffMin / 60;
  if (diffHr < 24) return `${Math.round(diffHr)} hrs ago`;
  const diffDay = diffHr / 24;
  if (diffDay < 7) return `${Math.round(diffDay)} days ago`;
  return new Date(iso).toLocaleDateString();
}
